/**
 * Type guards for drag-and-drop data validation
 * Provides runtime safety for drag data payloads
 */

import type { FieldType } from '../types/form';

/**
 * Check if a value is a valid FieldType
 */
export function isValidFieldType(type: unknown): type is FieldType {
  if (typeof type !== 'string') return false;

  const validTypes: FieldType[] = [
    'text',
    'email',
    'number',
    'textarea',
    'select',
    'checkbox',
    'radio',
    'date',
    'datetime',
    'file',
    'richtext',
    'signature',
    'repeatable',
    'section',
    'divider',
    'page_break',
    'layout',
    'phone',
    'rating',
    'slider',
    'url',
    'color',
    'hidden',
    'matrix',
    'ranking',
    'calculated',
    'address',
    'payment',
    'appointment',
    'nps',
    'country',
    'image_choice',
    'opinion_scale',
  ];

  return validTypes.includes(type as FieldType);
}

/**
 * Type guard for palette drag source data
 */
export interface PaletteDragData {
  isNew: true;
  fieldType: FieldType;
}

export function isPaletteDragData(data: unknown): data is PaletteDragData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.isNew === true &&
    'fieldType' in d &&
    isValidFieldType(d.fieldType)
  );
}

/**
 * Type guard for canvas field drag source data
 */
export interface CanvasFieldDragData {
  type: 'canvas-field';
  fieldId: string;
}

export function isCanvasFieldDragData(data: unknown): data is CanvasFieldDragData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'canvas-field' &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for reorder target drop data
 */
export interface ReorderTargetDropData {
  type: 'reorder-target';
  fieldId: string;
}

export function isReorderTargetDropData(data: unknown): data is ReorderTargetDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'reorder-target' &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for layout column drop data
 */
export interface LayoutColumnDropData {
  type: 'layout-column';
  layoutFieldId: string;
  columnIndex: number;
}

export function isLayoutColumnDropData(data: unknown): data is LayoutColumnDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'layout-column' &&
    typeof d.layoutFieldId === 'string' &&
    d.layoutFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex)
  );
}

/**
 * Type guard for layout column field drag data
 */
export interface LayoutColumnFieldDragData {
  type: 'layout-column-field';
  layoutFieldId: string;
  columnIndex: number;
  fieldId: string;
}

export function isLayoutColumnFieldDragData(data: unknown): data is LayoutColumnFieldDragData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'layout-column-field' &&
    typeof d.layoutFieldId === 'string' &&
    d.layoutFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex) &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for column field reorder drop data
 */
export interface ColumnFieldReorderDropData {
  type: 'column-field-reorder';
  layoutFieldId: string;
  columnIndex: number;
  fieldId: string;
}

export function isColumnFieldReorderDropData(data: unknown): data is ColumnFieldReorderDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'column-field-reorder' &&
    typeof d.layoutFieldId === 'string' &&
    d.layoutFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex) &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for repeatable column drop data
 */
export interface RepeatableColumnDropData {
  type: 'repeatable-column';
  repeatableFieldId: string;
  columnIndex: number;
}

export function isRepeatableColumnDropData(data: unknown): data is RepeatableColumnDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'repeatable-column' &&
    typeof d.repeatableFieldId === 'string' &&
    d.repeatableFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex)
  );
}

/**
 * Type guard for repeatable column field drag data
 */
export interface RepeatableColumnFieldDragData {
  type: 'repeatable-column-field';
  repeatableFieldId: string;
  columnIndex: number;
  fieldId: string;
}

export function isRepeatableColumnFieldDragData(data: unknown): data is RepeatableColumnFieldDragData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'repeatable-column-field' &&
    typeof d.repeatableFieldId === 'string' &&
    d.repeatableFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex) &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for repeatable field reorder drop data
 */
export interface RepeatableFieldReorderDropData {
  type: 'repeatable-field-reorder';
  repeatableFieldId: string;
  columnIndex: number;
  fieldId: string;
}

export function isRepeatableFieldReorderDropData(data: unknown): data is RepeatableFieldReorderDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'repeatable-field-reorder' &&
    typeof d.repeatableFieldId === 'string' &&
    d.repeatableFieldId.trim().length > 0 &&
    typeof d.columnIndex === 'number' &&
    Number.isInteger(d.columnIndex) &&
    d.columnIndex >= 0 &&
    !Number.isNaN(d.columnIndex) &&
    typeof d.fieldId === 'string' &&
    d.fieldId.trim().length > 0
  );
}

/**
 * Type guard for canvas drop data
 */
export interface CanvasDropData {
  type: 'canvas';
}

export function isCanvasDropData(data: unknown): data is CanvasDropData {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return d.type === 'canvas';
}

/**
 * Helper to safely extract string from drag data
 */
export function safeGetString(data: unknown, key: string): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Helper to safely extract number from drag data
 */
export function safeGetNumber(data: unknown, key: string): number | null {
  if (typeof data !== 'object' || data === null) return null;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'number' && value >= 0 ? value : null;
}
